/**
 * NotebookCanvas - Left panel displaying notebook blocks
 */

import React, { useCallback, useRef, useEffect, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { useNotebookStore, selectBlocks, selectBlockActions } from '../../stores/notebookStore.js'
import type { PortletBlock } from '../../stores/notebookStore.js'
import type { PortletConfig } from '../../types.js'
import type { ColorPalette } from '../../utils/colorPalettes.js'
import { getColorPalette } from '../../utils/colorPalettes.js'
import { ensureAnalysisConfig } from '../../utils/configMigration.js'
import NotebookPortletBlock from './NotebookPortletBlock.js'
import NotebookMarkdownBlock from './NotebookMarkdownBlock.js'
import PortletAnalysisModal from '../PortletAnalysisModal.js'
import { useTranslation } from '../../hooks/useTranslation.js'

/**
 * Derive the block update payload from saved portlet data.
 * Returns null when the data has no usable analysis config.
 */
function buildBlockUpdate(
  portletData: PortletConfig | Omit<PortletConfig, 'id' | 'x' | 'y'>,
): Partial<PortletBlock> | null {
  const { analysisConfig } = ensureAnalysisConfig(portletData as PortletConfig)
  if (!analysisConfig) return null

  const chartModeConfig = analysisConfig.charts[analysisConfig.analysisType]
  return {
    title: portletData.title,
    query: JSON.stringify(analysisConfig.query),
    chartType: chartModeConfig?.chartType || 'bar',
    chartConfig: chartModeConfig?.chartConfig,
    displayConfig: chartModeConfig?.displayConfig,
  }
}

const NotebookCanvas = React.memo(function NotebookCanvas({ colorPalette }: { colorPalette?: ColorPalette }) {
  const { t } = useTranslation()
  const resolvedPalette = colorPalette ?? getColorPalette()
  const blocks = useNotebookStore(selectBlocks)
  const { removeBlock, moveBlock, updateBlock } = useNotebookStore(useShallow(selectBlockActions))
  const endRef = useRef<HTMLDivElement>(null)

  // Edit modal state
  const [editingBlock, setEditingBlock] = useState<PortletBlock | null>(null)

  // Auto-scroll only when NEW blocks are added (not on initial load)
  const prevCountRef = useRef(blocks.length)
  useEffect(() => {
    if (blocks.length > prevCountRef.current) {
      endRef.current?.scrollIntoView({ behavior: 'smooth' })
    }
    prevCountRef.current = blocks.length
  }, [blocks.length])

  const handleRemove = useCallback((id: string) => removeBlock(id), [removeBlock])
  const handleMoveUp = useCallback((id: string) => moveBlock(id, 'up'), [moveBlock])
  const handleMoveDown = useCallback((id: string) => moveBlock(id, 'down'), [moveBlock])
  const handleEdit = useCallback((block: PortletBlock) => setEditingBlock(block), [])

  const handleEditSave = useCallback((portletData: PortletConfig | Omit<PortletConfig, 'id' | 'x' | 'y'>) => {
    if (!editingBlock) return

    const update = buildBlockUpdate(portletData)
    if (update) updateBlock(editingBlock.id, update)

    setEditingBlock(null)
  }, [editingBlock, updateBlock])

  if (blocks.length === 0) {
    return (
      <div className="dc:flex dc:items-center dc:justify-center dc:h-full">
        <div className="dc:text-center dc:max-w-sm dc:px-6">
          <h3 className="dc:text-base dc:font-semibold text-dc-text dc:mb-2">
            {t('notebook.canvas.emptyTitle')}
          </h3>
          <p className="dc:text-sm text-dc-text-secondary">
            {t('notebook.canvas.emptyDescription')}
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="dc:h-full dc:overflow-y-auto dc:p-4">
      {blocks.map((block, index) => {
        const isFirst = index === 0
        const isLast = index === blocks.length - 1

        if (block.type === 'portlet') {
          return (
            <NotebookPortletBlock
              key={block.id}
              block={block}
              colorPalette={resolvedPalette}
              onRemove={handleRemove}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              onEdit={handleEdit}
              isFirst={isFirst}
              isLast={isLast}
            />
          )
        }

        if (block.type === 'markdown') {
          return (
            <NotebookMarkdownBlock
              key={block.id}
              block={block}
              onRemove={handleRemove}
              onMoveUp={handleMoveUp}
              onMoveDown={handleMoveDown}
              isFirst={isFirst}
              isLast={isLast}
            />
          )
        }

        return null
      })}
      <div ref={endRef} />

      {/* Edit modal */}
      <PortletAnalysisModal
        isOpen={!!editingBlock}
        onClose={() => setEditingBlock(null)}
        onSave={handleEditSave}
        colorPalette={resolvedPalette}
        portlet={editingBlock ? {
          id: editingBlock.id,
          title: editingBlock.title,
          query: editingBlock.query,
          chartType: editingBlock.chartType,
          chartConfig: editingBlock.chartConfig,
          displayConfig: editingBlock.displayConfig,
          w: 5, h: 4, x: 0, y: 0,
        } : null}
        title={t('notebook.canvas.editVisualization')}
        submitText={t('notebook.canvas.update')}
      />
    </div>
  )
})

export default NotebookCanvas
